import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion, LayoutGroup } from "framer-motion";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";

import {
  SiReact, SiFirebase, SiJavascript, SiPython, SiVuedotjs, SiTypescript,
} from "react-icons/si";
import {
  ChevronLeft, ChevronRight, ArrowLeft,
  ExternalLink, Linkedin, Instagram, Mail, Github, X as XIcon,
} from "lucide-react";

/* ─────────────────────────────────────
   TECH ICONS
───────────────────────────────────── */
const techIcons = {
  React: <SiReact style={{ color: "#61DAFB", fontSize: 13 }} />,
  Firebase: <SiFirebase style={{ color: "#FFCA28", fontSize: 13 }} />,
  Javascript: <SiJavascript style={{ color: "#F7DF1E", fontSize: 13 }} />,
  Python: <SiPython style={{ color: "#3776AB", fontSize: 13 }} />,
  Vue: <SiVuedotjs style={{ color: "#42B883", fontSize: 13 }} />,
  Typescript: <SiTypescript style={{ color: "#3178C6", fontSize: 13 }} />,
  GSAP: <img src="https://cdn.worldvectorlogo.com/logos/gsap-greensock.svg" alt="GSAP" className="w-[14px] h-[14px] object-contain" />,
  Vite: <img src="https://vitejs.dev/logo.svg" alt="Vite" className="w-[14px] h-[14px] object-contain" />,
  Tailwind: <img src="https://cdn.worldvectorlogo.com/logos/tailwind-css-2.svg" alt="Tailwind CSS" className="w-[14px] h-[14px] object-contain" />,
  Html5: <img src="https://cdn.worldvectorlogo.com/logos/html-1.svg" alt="HTML5" className="w-[14px] h-[14px] object-contain" />,
  CSS3: <img src="https://cdn.worldvectorlogo.com/logos/css-3.svg" alt="CSS3" className="w-[14px] h-[14px] object-contain" />,
  Bootstrap: <img src="https://cdn.worldvectorlogo.com/logos/bootstrap-5-1.svg" alt="Bootstrap" className="w-[14px] h-[14px] object-contain" />,
};

/* ─────────────────────────────────────
   HIDE SCROLLBAR
───────────────────────────────────── */
const HideScrollStyle = () => (
  <style>{`
    .hide-scrollbar{scrollbar-width:none;-ms-overflow-style:none}
    .hide-scrollbar::-webkit-scrollbar{display:none}
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `}</style>
);

/* ─────────────────────────────────────
   SPRING CONFIGS
───────────────────────────────────── */
const SPRING_BOUNCY  = { type: "spring", stiffness: 420, damping: 22, mass: 0.8 };
const SPRING_SOFT    = { type: "spring", stiffness: 280, damping: 26, mass: 0.9 };
const SPRING_GENTLE  = { type: "spring", stiffness: 200, damping: 28 };
// Calm spring used for detail-page interactive elements (buttons, tech pills, nav)
const SPRING_CALM    = { type: "spring", stiffness: 180, damping: 32, mass: 1 };

/* ─────────────────────────────────────
   DATA
───────────────────────────────────── */
const cards = [
  {
    id: "aquai", num: "01", title: "AquAi",
    category: "Frontend Developer", filters: ["Development"],
    desc: "AquAi is an AI-powered smart aquaponics platform designed to automate and optimize the cultivation of tilapia and kangkong. The system integrates IoT sensors, machine learning, and computer vision to provide real-time monitoring, predictive analytics, and automated decision-making.",
    img: "/images/aquAi.png", tech: ["React", "Vite", "Firebase"],
    github: "https://github.com/AudreyTan03/AquAi", live: "aquaponics-d8dcc.web.app/",
    screenshots: ["/images/thesis.png","/images/thesis 2.png","/images/pishdalogin.jpg","/images/thesis 3.png","/images/thesis 4.png","/images/thesis 5.png","/images/thesis 6 chatbot.png","/images/pishdaforgetpass.jpg"],
    year: "2026", role: "Frontend Developer",
    accent: "#fb923c", accentBg: "rgba(251,146,60,0.08)", accentBorder: "rgba(251,146,60,0.25)",
  },
  {
    id: "GTA", num: "02", title: "GTA",
    category: "Frontend Developer", filters: ["Development"],
    desc: "A high-performance GTA VI-inspired landing experience built using React, Tailwind CSS, and GSAP. It combines advanced animation techniques with responsive UI design to deliver smooth transitions, interactive storytelling, and a visually striking interface.",
    img: "/images/gta 6 logo.jpg", tech: ["React", "GSAP", "Tailwind"],
    github: "https://github.com/AudreyTan03/GTA-VI", live: "gta-vi-xi-eight.vercel.app/",
    screenshots: ["/images/gta 1.png","/images/gta 2.png","/images/gta 3.png","/images/gta 4.png","/images/gta 5.png","/images/gta 6.png","/images/gta 7.png","/images/gta 8.png","/images/gta 9.png","/images/gta end.png"],
    year: "2025", role: "Frontend Developer",
    accent: "#c084fc", accentBg: "rgba(192,132,252,0.08)", accentBorder: "rgba(192,132,252,0.25)",
  },
  {
    id: "engr-bato", num: "03", title: "Engr Bato",
    category: "Frontend Developer", filters: ["Development"],
    desc: "Engr. Bato is an AI-powered construction management platform that streamlines planning, scheduling, and progress tracking — helping teams build smarter, faster, and with confidence.",
    img: "/images/Engr bato.jpg", tech: ["Typescript", "Tailwind"],
    github: "https://github.com/your-repo", live: "https://your-live-demo.com",
    screenshots: ["/images/engr bato 1.png","/images/engr bato 2.png","/images/engr bato 3.png","/images/engr bato 4.png","/images/engr bato 5.png","/images/engr bato 6.png","/images/engr bato 7.png","/images/engr bato 8.png"],
    year: "2025", role: "Frontend Developer",
    accent: "#f472b6", accentBg: "rgba(244,114,182,0.08)", accentBorder: "rgba(244,114,182,0.25)",
  },
  {
    id: "porthub", num: "04", title: "PortHub",
    category: "UI/UX Designer & Developer", filters: ["Development"],
    desc: "PortHub is a customizable web platform that allows users to create and personalize their own websites in real time. Built with a focus on flexibility and user experience, enabling dynamic layout adjustments and content customization.",
    img: "/images/porthub.jpg", tech: ["React", "Javascript", "Tailwind"],
    github: "https://github.com/your-repo", live: "https://your-live-demo.com",
    screenshots: ["/images/portyland.jpg","/images/portyregis.jpg","/images/portyland1.jpg","/images/portydash.jpg","/images/portyside.jpg","/images/portyside1.jpg","/images/portyside2.jpg","/images/portyprof.jpg"],
    year: "2024", role: "UI/UX Designer & Developer",
    accent: "#38bdf8", accentBg: "rgba(56,189,248,0.08)", accentBorder: "rgba(56,189,248,0.25)",
  },
  {
    id: "casitas", num: "05", title: "Casitas Birria",
    category: "Frontend Dev", filters: ["Development"],
    desc: "A full-featured restaurant website for Casitas Birria. Covers online menu browsing, brand storytelling, and a seamless ordering experience — warm, vibrant, and distinctly local.",
    img: "/images/casitas.jpg", tech: ["Html5", "CSS3", "Bootstrap"],
    github: "https://github.com/your-repo", live: "https://your-live-demo.com",
    screenshots: ["/images/homepage.png","/images/homepage1.png","/images/homepage3.png","/images/homepage4.png","/images/aboutus.png","/images/aboutus5.png","/images/menu.png","/images/menu1.png"],
    year: "2024", role: "Frontend Developer",
    accent: "#22c55e", accentBg: "rgba(34,197,94,0.08)", accentBorder: "rgba(34,197,94,0.25)",
  },
];

const FILTERS = ["All", "Development", "Design"];

/* ─────────────────────────────────────
   FILTER TABS — bouncy pill indicator
───────────────────────────────────── */
function FilterTabs({ active, onChange }) {
  return (
    <LayoutGroup>
      <div className="relative flex items-center gap-2">
        {FILTERS.map((f) => {
          const isActive = active === f;
          return (
            <motion.button
              key={f}
              onClick={() => onChange(f)}
              animate={{
                paddingLeft:   isActive ? "32px" : "22px",
                paddingRight:  isActive ? "32px" : "22px",
                paddingTop:    isActive ? "13px" : "11px",
                paddingBottom: isActive ? "13px" : "11px",
                fontSize:      isActive ? "16px" : "15px",
              }}
              transition={SPRING_BOUNCY}
              className="relative rounded-full font-semibold focus:outline-none select-none z-10"
              style={{ fontFamily: "Geist, sans-serif" }}
            >
              {isActive && (
                <motion.span
                  layoutId="filter-pill"
                  className="absolute inset-0 rounded-full bg-zinc-800 dark:bg-zinc-700 shadow-[0_4px_20px_rgba(0,0,0,0.25)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.6)]"
                  transition={SPRING_BOUNCY}
                />
              )}
              <motion.span
                animate={{ scale: isActive ? 1.04 : 1 }}
                transition={SPRING_BOUNCY}
                className={`relative z-10 block ${isActive ? "text-white" : "text-zinc-500 dark:text-zinc-400"}`}
              >
                {f}
              </motion.span>
            </motion.button>
          );
        })}
      </div>
    </LayoutGroup>
  );
}

/* ─────────────────────────────────────
   LIGHTBOX
───────────────────────────────────── */
function Lightbox({ open, onClose, images, index, setIndex, next, prev, title }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose, next, prev]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-md"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
        >
          <motion.div
            className="absolute inset-0 flex items-center justify-center p-4"
            initial={{ scale: 0.92, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 10, opacity: 0 }}
            transition={SPRING_SOFT}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full max-w-5xl aspect-[16/9] rounded-2xl overflow-hidden bg-white dark:bg-[#0d0f14] border border-zinc-200 dark:border-white/10">
              <img src={images[index]} alt={`${title ?? "Image"} ${index + 1}`} className="h-full w-full object-contain" loading="eager" />
              <div className="absolute left-4 top-4 text-zinc-900 dark:text-white/90 text-xs font-jetbrains px-3 py-1.5 rounded-full bg-white/80 dark:bg-black/40 border border-zinc-200 dark:border-white/10 backdrop-blur">
                {title} — {index + 1} / {images.length}
              </div>
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={SPRING_BOUNCY}
                className="absolute right-3 top-3 h-9 w-9 grid place-items-center rounded-full bg-black/10 hover:bg-black/20 dark:bg-black/40 dark:hover:bg-black/60 border border-zinc-200/60 dark:border-white/10"
              >
                <XIcon className="h-4 w-4 text-zinc-900 dark:text-white" />
              </motion.button>
              {images.length > 1 && (
                <>
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <motion.button
                      onClick={prev}
                      whileHover={{ scale: 1.06 }}
                      whileTap={{ scale: 0.94 }}
                      transition={SPRING_CALM}
                      className="h-10 w-10 grid place-items-center rounded-full bg-black/10 hover:bg-black/20 dark:bg-black/40 dark:hover:bg-black/60 border border-zinc-200/60 dark:border-white/10"
                    >
                      <ChevronLeft className="h-5 w-5 text-zinc-900 dark:text-white" />
                    </motion.button>
                  </div>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <motion.button
                      onClick={next}
                      whileHover={{ scale: 1.06 }}
                      whileTap={{ scale: 0.94 }}
                      transition={SPRING_CALM}
                      className="h-10 w-10 grid place-items-center rounded-full bg-black/10 hover:bg-black/20 dark:bg-black/40 dark:hover:bg-black/60 border border-zinc-200/60 dark:border-white/10"
                    >
                      <ChevronRight className="h-5 w-5 text-zinc-900 dark:text-white" />
                    </motion.button>
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                    {images.map((_, i) => (
                      <motion.button
                        key={i}
                        onClick={() => setIndex(i)}
                        animate={{
                          width: i === index ? 20 : 6,
                          backgroundColor: i === index ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.3)",
                          scale: i === index ? 1.1 : 1,
                        }}
                        transition={SPRING_BOUNCY}
                        className="h-1.5 rounded-full"
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─────────────────────────────────────
   PROJECT CARD — with bouncy hover & spring entrance
───────────────────────────────────── */
function ProjectCard({ card, onSelect, index }) {
  const isPrivate = card.github === "https://github.com/your-repo";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 28, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -16, scale: 0.95 }}
      transition={{ ...SPRING_SOFT, delay: index * 0.055 }}
      whileHover={{ y: -6, scale: 1.015 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(card.id)}
      className="group relative flex flex-col rounded-2xl overflow-hidden cursor-pointer
                 bg-white dark:bg-[#0f1014]
                 border border-zinc-200 dark:border-white/[0.07]
                 shadow-[0_4px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
      style={{ transformOrigin: "center bottom" }}
    >
      {/* Glow on hover */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none z-10"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        style={{ boxShadow: `0 0 0 1.5px ${card.accentBorder}, 0 20px 60px ${card.accentBg}` }}
      />

      {/* Image */}
      <div className="relative overflow-hidden h-[200px] bg-zinc-100 dark:bg-zinc-900">
        <img
          src={card.img} alt={card.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent dark:from-black/60 dark:via-black/10" />

        {/* Category pill */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={SPRING_BOUNCY}
          className="absolute top-3 left-3 text-[10px] font-jetbrains font-bold tracking-widest uppercase px-3 py-1 rounded-full"
          style={{ background: card.accentBg, border: `1px solid ${card.accentBorder}`, color: card.accent }}
        >
          {card.category}
        </motion.div>

        {/* Index */}
        <div className="absolute top-3 right-3 text-[10px] font-jetbrains font-bold tracking-[0.18em] text-white/50 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-md">
          {card.num}
        </div>

        {/* Filter tags */}
        <div className="absolute bottom-3 left-3 flex gap-1.5">
          {card.filters.map((f) => (
            <span key={f} className="text-[9px] font-jetbrains font-semibold tracking-widest uppercase px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-sm border border-white/10 text-white/70">
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-3 p-5 flex-1">
        <div>
          <h3 className="font-[Geist] font-semibold text-[18px] tracking-tight text-zinc-900 dark:text-white leading-tight">{card.title}</h3>
          <p className="mt-0.5 text-[11px] font-jetbrains text-zinc-500 dark:text-white/40 tracking-wide">{card.year} · {card.role}</p>
        </div>
        <p className="text-[13px] font-jetbrains leading-relaxed text-zinc-600 dark:text-white/60 line-clamp-3 flex-1">{card.desc}</p>
        <div className="h-px bg-zinc-100 dark:bg-white/[0.06] mt-1" />
        <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
          {card.tech.map((t, i) => (
            <motion.span
              key={i}
              whileHover={{ scale: 1.08, y: -1 }}
              transition={SPRING_BOUNCY}
              className="inline-flex items-center gap-1.5 text-[11px] font-jetbrains px-2.5 py-1 rounded-full bg-zinc-100 dark:bg-white/[0.05] border border-zinc-200 dark:border-white/[0.08] text-zinc-600 dark:text-white/60"
            >
              {techIcons[t]}{t}
            </motion.span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────
   PROJECT GRID
───────────────────────────────────── */
function ProjectGrid({ onSelect }) {
  const [activeFilter, setActiveFilter] = useState("All");

  const displayedCards = useMemo(() =>
    activeFilter === "All" ? cards : cards.filter((c) => c.filters.includes(activeFilter)),
    [activeFilter]
  );

  return (
    <div>
      {/* Header row: title left, filters right */}
      <div className="mb-10">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          {/* Left: title block */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="text-[12px] tracking-[0.18em] uppercase text-emerald-700 dark:text-lime-300/90 font-jetbrains mb-4"
            >
              MY PROJECTS
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-[Geist] font-semibold tracking-tight text-zinc-900 dark:text-white leading-[1.05]"
              style={{ fontSize: "clamp(36px, 6vw, 72px)" }}
            >
              Things I've Built
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22 }}
              className="mt-4 text-[14px] font-jetbrains text-zinc-600 dark:text-white/50 max-w-sm"
            >
              Designing and developing next-level digital products
            </motion.p>
          </div>

          {/* Right: filter tabs aligned to bottom */}
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="flex items-end pb-1 self-end"
          >
            <FilterTabs active={activeFilter} onChange={setActiveFilter} />
          </motion.div>
        </div>
      </div>

      {/* Grid with AnimatePresence for card exit/enter */}
      <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <AnimatePresence mode="popLayout">
          {displayedCards.map((card, i) => (
            <ProjectCard key={card.id} card={card} onSelect={onSelect} index={i} />
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {displayedCards.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={SPRING_SOFT}
            className="flex flex-col items-center justify-center py-28 gap-3"
          >
            <motion.span
              animate={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-4xl"
            >🔍</motion.span>
            <p className="font-[Geist] font-semibold text-zinc-800 dark:text-white text-lg">No projects yet</p>
            <p className="font-jetbrains text-[13px] text-zinc-500 dark:text-white/40">Nothing tagged as "{activeFilter}" yet.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─────────────────────────────────────
   PROJECT CAROUSEL
───────────────────────────────────── */
function ProjectCarousel({ images, title, accent }) {
  const [idx, setIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [direction, setDirection] = useState(1);
  const total = images.length;

  const prev = useCallback(() => {
    setDirection(-1);
    setIdx((i) => (i - 1 + total) % total);
  }, [total]);

  const next = useCallback(() => {
    setDirection(1);
    setIdx((i) => (i + 1) % total);
  }, [total]);

  const goTo = (i) => {
    setDirection(i > idx ? 1 : -1);
    setIdx(i);
  };

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? "8%" : "-8%", opacity: 0, scale: 0.98 }),
    center: { x: 0, opacity: 1, scale: 1 },
    exit: (dir) => ({ x: dir > 0 ? "-8%" : "8%", opacity: 0, scale: 0.98 }),
  };

  return (
    <>
      <Lightbox open={lightboxOpen} onClose={() => setLightboxOpen(false)} images={images} index={idx} setIndex={setIdx} next={next} prev={prev} title={title} />
      <div className="relative w-full flex flex-col gap-4">
        <div
          className="relative w-full rounded-2xl overflow-hidden border border-zinc-200 dark:border-white/10 bg-zinc-100 dark:bg-black/20 cursor-zoom-in"
          onClick={() => setLightboxOpen(true)}
        >
          {/* Browser bar */}
          <div className="h-7 flex items-center px-3 gap-2 bg-zinc-50 dark:bg-white/[0.03] border-b border-zinc-200 dark:border-white/[0.07]">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
            </div>
            <div className="flex-1 h-3.5 rounded bg-zinc-200 dark:bg-white/[0.07] flex items-center px-2">
              <span className="text-[9px] font-jetbrains text-zinc-400 dark:text-white/20">your-project.vercel.app</span>
            </div>
          </div>

          <div className="relative w-full aspect-[16/10] overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.img
                key={idx}
                src={images[idx]}
                alt={`${title} screenshot ${idx + 1}`}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={SPRING_SOFT}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

            {total > 1 && (
              <>
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <motion.button
                    onClick={(e) => { e.stopPropagation(); prev(); }}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.94 }}
                    transition={SPRING_CALM}
                    className="h-9 w-9 grid place-items-center rounded-full bg-black/40 hover:bg-black/70 border border-white/10 backdrop-blur-sm"
                  >
                    <ChevronLeft className="h-4 w-4 text-white/70" />
                  </motion.button>
                </div>
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <motion.button
                    onClick={(e) => { e.stopPropagation(); next(); }}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.94 }}
                    transition={SPRING_CALM}
                    className="h-9 w-9 grid place-items-center rounded-full bg-black/40 hover:bg-black/70 border border-white/10 backdrop-blur-sm"
                  >
                    <ChevronRight className="h-4 w-4 text-white/70" />
                  </motion.button>
                </div>
              </>
            )}
            <div className="absolute bottom-3 right-3 text-[11px] font-medium px-2 py-1 rounded-md bg-black/40 text-white/70 border border-white/10 backdrop-blur-sm">
              {idx + 1} / {total}
            </div>
          </div>
        </div>

        {/* Dot indicators */}
        {total > 1 && (
          <div className="flex justify-center gap-2">
            {images.map((_, i) => (
              <motion.button
                key={i}
                onClick={() => goTo(i)}
                animate={{
                  width: i === idx ? 24 : 8,
                  backgroundColor: i === idx ? accent : "rgba(161,161,170,0.4)",
                  scale: i === idx ? 1 : 0.85,
                }}
                whileHover={{ scale: 1.3 }}
                whileTap={{ scale: 0.85 }}
                transition={SPRING_BOUNCY}
                className="h-2 rounded-full"
              />
            ))}
          </div>
        )}

        {/* Thumbnails */}
        {total > 1 && (
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
            {images.map((img, i) => (
              <motion.button
                key={i}
                onClick={() => goTo(i)}
                whileHover={{ scale: 1.06, y: -2 }}
                whileTap={{ scale: 0.94 }}
                transition={SPRING_BOUNCY}
                animate={{
                  borderColor: i === idx ? accent : "rgba(255,255,255,0.1)",
                  opacity: i === idx ? 1 : 0.5,
                }}
                className="shrink-0 h-16 w-24 rounded-lg overflow-hidden border-2"
              >
                <img src={img} alt="" className="h-full w-full object-cover" />
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────
   PROJECT DETAIL
   Mobile order: back btn → carousel (image) → details → prev/next nav
   Desktop order: left (details) | right (carousel sticky)
───────────────────────────────────── */
function ProjectDetail({ project, onBack, onNav }) {
  const hasLive = project.live && project.live !== "https://your-live-demo.com";
  const currentIdx = cards.findIndex((c) => c.id === project.id);
  const prevProject = currentIdx > 0 ? cards[currentIdx - 1] : null;
  const nextProject = currentIdx < cards.length - 1 ? cards[currentIdx + 1] : null;
  const allImages = project.screenshots?.length ? [project.img, ...project.screenshots] : [project.img];
  const isPrivate = project.github === "https://github.com/your-repo";

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: SPRING_SOFT },
  };

  /* Shared detail content (used in both mobile and desktop) */
  const DetailMeta = () => (
    <motion.div variants={itemVariants} className="flex items-center gap-3">
      <span className="font-jetbrains text-[11px] font-bold tracking-[0.2em] text-zinc-400 dark:text-white/30">{project.num}</span>
      <div className="h-px w-7 bg-zinc-200 dark:bg-white/20" />
      <span className="font-jetbrains text-[11px] tracking-[0.1em] uppercase text-zinc-500 dark:text-white/40">{project.role}</span>
    </motion.div>
  );

  const DetailTitle = () => (
    <motion.h1
      variants={itemVariants}
      className="font-[Geist] font-semibold tracking-tight text-zinc-900 dark:text-white leading-[1.05]"
      style={{ fontSize: "clamp(32px,4.5vw,56px)" }}
    >
      {project.title}
    </motion.h1>
  );

  const DetailFilters = () => (
    <motion.div variants={itemVariants} className="flex gap-2 flex-wrap">
      {project.filters.map((f) => (
        <motion.span
          key={f}
          whileHover={{ scale: 1.04 }}
          transition={SPRING_CALM}
          className="text-[11px] font-jetbrains font-semibold tracking-widest uppercase px-3 py-1 rounded-full border cursor-default"
          style={{ background: project.accentBg, borderColor: project.accentBorder, color: project.accent }}
        >
          {f}
        </motion.span>
      ))}
    </motion.div>
  );

  const DetailDesc = () => (
    <motion.p
      variants={itemVariants}
      className="font-jetbrains text-[14px] leading-relaxed text-zinc-600 dark:text-white/60 max-w-[46ch]"
    >
      {project.desc}
    </motion.p>
  );

  const DetailTech = () => (
    <motion.div variants={itemVariants} className="flex flex-wrap gap-2">
      {project.tech.map((t, i) => (
        <motion.span
          key={i}
          whileHover={{ scale: 1.04, y: -1 }}
          whileTap={{ scale: 0.97 }}
          transition={SPRING_CALM}
          className="inline-flex items-center gap-1.5 font-jetbrains text-[12px] px-3 py-1.5 rounded-full bg-zinc-100 dark:bg-white/[0.05] border border-zinc-200 dark:border-white/[0.09] text-zinc-700 dark:text-white/70 cursor-default"
        >
          {techIcons[t]} {t}
        </motion.span>
      ))}
    </motion.div>
  );

  const DetailButtons = () => (
    <motion.div variants={itemVariants} className="flex gap-3 flex-wrap">
      <motion.a
        href={hasLive ? project.live : undefined}
        target="_blank" rel="noreferrer"
        whileHover={hasLive ? { y: -2, scale: 1.02 } : {}}
        whileTap={hasLive ? { scale: 0.97 } : {}}
        transition={SPRING_CALM}
        onClick={(e) => { if (!hasLive) e.preventDefault(); }}
        className={`inline-flex items-center gap-2 font-jetbrains text-[13px] font-medium px-5 py-2.5 rounded-xl transition-colors ${
          hasLive
            ? "bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:bg-zinc-700 dark:hover:bg-zinc-100"
            : "bg-zinc-200 dark:bg-white/10 text-zinc-400 dark:text-white/30 cursor-not-allowed"
        }`}
      >
        <ExternalLink size={13} />{hasLive ? "Live Demo" : "Unavailable"}
      </motion.a>

      <motion.a
        href={isPrivate ? undefined : project.github}
        target="_blank" rel="noreferrer"
        whileHover={{ y: -2, scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        transition={SPRING_CALM}
        onClick={(e) => { if (isPrivate) e.preventDefault(); }}
        style={{ opacity: isPrivate ? 0.45 : 1 }}
        className="inline-flex items-center gap-2 font-jetbrains text-[13px] font-medium px-5 py-2.5 rounded-xl border border-zinc-200 dark:border-white/[0.12] text-zinc-700 dark:text-white/70 hover:bg-zinc-50 dark:hover:bg-white/[0.05] hover:text-zinc-900 dark:hover:text-white transition-colors"
      >
        <Github size={13} />{isPrivate ? "Private" : "GitHub"}
      </motion.a>
    </motion.div>
  );

  const DetailDivider = () => (
    <motion.div variants={itemVariants} className="h-px bg-zinc-100 dark:bg-white/[0.07]" />
  );

  const DetailNav = () => (
    <motion.div variants={itemVariants} className="grid grid-cols-2 gap-3">
      {prevProject ? (
        <motion.button
          onClick={() => onNav(prevProject.id)}
          whileHover={{ x: -2, scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          transition={SPRING_CALM}
          className="flex items-center gap-3 p-3 rounded-xl border border-zinc-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:bg-zinc-50 dark:hover:bg-white/[0.05] hover:border-zinc-300 dark:hover:border-white/[0.13] text-left"
        >
          <ChevronLeft size={14} className="text-zinc-400 dark:text-white/30 flex-shrink-0" />
          <div>
            <p className="font-jetbrains text-[9px] tracking-widest uppercase text-zinc-400 dark:text-white/30 mb-0.5">Prev</p>
            <p className="font-[Geist] text-[13px] font-medium text-zinc-700 dark:text-white/70 leading-tight">{prevProject.title}</p>
          </div>
        </motion.button>
      ) : <div />}

      {nextProject ? (
        <motion.button
          onClick={() => onNav(nextProject.id)}
          whileHover={{ x: 2, scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          transition={SPRING_CALM}
          className="flex items-center justify-end gap-3 p-3 rounded-xl border border-zinc-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:bg-zinc-50 dark:hover:bg-white/[0.05] hover:border-zinc-300 dark:hover:border-white/[0.13] text-right"
        >
          <div>
            <p className="font-jetbrains text-[9px] tracking-widest uppercase text-zinc-400 dark:text-white/30 mb-0.5">Next</p>
            <p className="font-[Geist] text-[13px] font-medium text-zinc-700 dark:text-white/70 leading-tight">{nextProject.title}</p>
          </div>
          <ChevronRight size={14} className="text-zinc-400 dark:text-white/30 flex-shrink-0" />
        </motion.button>
      ) : <div />}
    </motion.div>
  );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
    >
      {/* Back button */}
      <motion.button
        onClick={onBack}
        whileHover={{ x: -3, scale: 1.01 }}
        whileTap={{ scale: 0.97 }}
        transition={SPRING_CALM}
        className="inline-flex items-center gap-2 font-jetbrains text-[13px] text-zinc-500 dark:text-white/40 hover:text-zinc-900 dark:hover:text-white transition-colors mb-8"
      >
        <ArrowLeft size={14} />All projects
      </motion.button>

      {/* ── MOBILE LAYOUT: meta → image → details → nav ── */}
      {/* Hidden on lg+, shown on mobile/tablet */}
      <div className="flex flex-col gap-6 lg:hidden">
        {/* 1. 01 · FRONTEND DEVELOPER — stays just below back button */}
        <DetailMeta />

        {/* 2. Carousel (image) */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ ...SPRING_SOFT, delay: 0.1 }}
        >
          <ProjectCarousel images={allImages} title={project.title} accent={project.accent} />
        </motion.div>

        {/* 3. Rest of details */}
        <DetailTitle />
        <DetailFilters />
        <DetailDesc />
        <DetailTech />
        <DetailButtons />
        <DetailDivider />

        {/* 3. Prev/Next nav */}
        <DetailNav />
      </div>

      {/* ── DESKTOP LAYOUT: left (details) | right (carousel sticky) ── */}
      {/* Hidden on mobile, shown on lg+ */}
      <div className="hidden lg:grid grid-cols-2 gap-12 lg:gap-16 items-start">
        {/* LEFT — details */}
        <div className="flex flex-col gap-6">
          <DetailMeta />
          <DetailTitle />
          <DetailFilters />
          <DetailDesc />
          <DetailTech />
          <DetailButtons />
          <DetailDivider />
          <DetailNav />
        </div>

        {/* RIGHT — Carousel sticky */}
        <motion.div
          initial={{ opacity: 0, x: 24, scale: 0.97 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ ...SPRING_SOFT, delay: 0.2 }}
          className="lg:sticky lg:top-24"
        >
          <ProjectCarousel images={allImages} title={project.title} accent={project.accent} />
        </motion.div>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────
   PAGE ROOT
───────────────────────────────────── */
export default function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams]);

  const selectedId = searchParams.get("project");
  const selectedProject = cards.find((p) => p.id === selectedId) || null;

  return (
    <div className="min-h-screen bg-white dark:bg-[#080809]">
      <HideScrollStyle />

      {/* Content */}
      <div className="mx-auto max-w-6xl px-6 pt-24 pb-16">
        <AnimatePresence mode="wait">
          {selectedProject ? (
            <ProjectDetail
              key={selectedProject.id}
              project={selectedProject}
              onBack={() => setSearchParams({})}
              onNav={(id) => setSearchParams({ project: id })}
            />
          ) : (
            <ProjectGrid key="grid" onSelect={(id) => setSearchParams({ project: id })} />
          )}
        </AnimatePresence>
      </div>

      {/* 🔥 CONTACT SECTION */}
      <section
        id="contact"
        className="mx-auto w-full max-w-6xl px-6 pt-12 pb-6"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="rounded-[28px] bg-white dark:bg-[#0f1014]
                     border border-zinc-200 dark:border-white/5
                     shadow-[0_12px_40px_rgba(0,0,0,.08)]
                     dark:shadow-[0_12px_40px_rgba(0,0,0,.25)]
                     px-6 sm:px-10 py-14 text-center"
        >
          {/* PILL */}
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5
                          bg-emerald-50 dark:bg-white/5
                          border border-emerald-200 dark:border-white/10
                          text-[11px] tracking-wide text-emerald-700 dark:text-white/80">
            <span className="inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            Available for work
          </div>

          {/* TITLE */}
          <h2
            className="mt-5 font-[Geist] font-semibold leading-[1.1]
                       text-zinc-900 dark:text-white"
            style={{ fontSize: "clamp(26px, 4vw, 42px)" }}
          >
            Let's create your
            <br className="hidden sm:block" /> next big idea.
          </h2>

          {/* BUTTON WITH FILL ANIMATION */}
          <div className="mt-7 flex justify-center">
            <motion.div
              className="relative group"
              whileHover={{ scale: 1.07 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.a
                href="https://mail.google.com/mail/?view=cm&to=audreyannetan13@gmail.com"
                target="_blank"
                rel="noreferrer"
                initial="rest"
                whileHover="hover"
                whileTap="tap"
                variants={{
                  rest: { scale: 1 },
                  hover: { scale: 1.03 },
                  tap: { scale: 0.96 },
                }}
                className="relative inline-flex items-center justify-center
                           rounded-full px-6 py-4 overflow-hidden
                           border border-zinc-200 dark:border-white/15
                           text-zinc-800 dark:text-white
                           shadow-[0_6px_20px_rgba(0,0,0,0.08)]
                           hover:shadow-[0_10px_30px_rgba(0,0,0,0.15)]"
              >
                {/* FILL */}
                <motion.span
                  variants={{
                    rest: { y: "100%" },
                    hover: { y: "0%" },
                  }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 bg-white dark:bg-white"
                />

                {/* TEXT */}
                <div className="relative z-10 overflow-hidden h-[20px] flex items-center">
                  <motion.span
                    variants={{
                      rest: { y: 0, color: "#ffffff" },
                      hover: { y: -20, color: "#111111" },
                    }}
                    transition={{ duration: 0.3 }}
                    className="block font-medium"
                  >
                    Contact Me
                  </motion.span>

                  <motion.span
                    variants={{
                      rest: { y: 20, color: "#ffffff" },
                      hover: { y: 0, color: "#111111" },
                    }}
                    transition={{ duration: 0.35 }}
                    className="absolute left-0 font-medium"
                  >
                    Contact Me
                  </motion.span>
                </div>
              </motion.a>
            </motion.div>
          </div>
        </motion.div>

        {/* SOCIALS WITH TOOLTIP */}
        <div className="mt-6 flex items-center justify-between text-zinc-600 dark:text-white/60 text-sm">
          <p className="font-jetbrains opacity-80">
            © 2026 Audrey Tan. All rights reserved.
          </p>

          <div className="flex items-center gap-5">
            {[
              { href: "https://www.linkedin.com/in/audrey-tan-72a514351/", icon: <Linkedin className="h-5 w-5" />, label: "LinkedIn" },
              { href: "https://github.com/AudreyTan03", icon: <Github className="h-5 w-5" />, label: "GitHub" },
              { href: "https://www.instagram.com/shob.hi111/", icon: <Instagram className="h-5 w-5" />, label: "Instagram" },
              { href: "https://mail.google.com/mail/?view=cm&to=audreyannetan13@gmail.com", icon: <Mail className="h-5 w-5" />, label: "Email" },
            ].map((item, i) => (
              <a
                key={i}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="group relative flex items-center justify-center
                           hover:text-zinc-900 dark:hover:text-white transition"
              >
                {item.icon}

                {/* TOOLTIP */}
                <span
                  className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2
                             whitespace-nowrap px-3 py-1 text-[11px]
                             rounded-full bg-white/90 dark:bg-zinc-900/90
                             text-zinc-800 dark:text-white
                             border border-zinc-200/70 dark:border-white/10
                             shadow-[0_6px_20px_rgba(0,0,0,0.08)]
                             opacity-0 scale-95 translate-y-1
                             group-hover:opacity-100 group-hover:scale-100 group-hover:translate-y-0
                             transition-all duration-200"
                >
                  {item.label}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}